import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GachaFrame from './GachaFrame'
import { fetchVoucher, redeemVoucher, GachaVoucher as Voucher } from './gachaApi'

/**
 * 당첨 교환권 (/event/gacha/voucher).
 *   5-1 교환권 — 배경 'TASTEE' 글자가 흐르고 시계 초가 간다(캡처 방지 보조 수단)
 *   5-2 확인창 — '사용하기' 를 누르면 한 번 더 묻는다
 *   5-3 사용 완료 — 글자·시계 멈춤, 회색, 도장, 사용 시각
 * 지금 사용 처리는 mock(로컬 상태). 실제로는 서버가 1회만 처리한다(gachaApi.redeemVoucher).
 * 당첨 기록이 없으면 화면 없이 /event/gacha 로 보낸다.
 */

const IS_DEV = process.env.NODE_ENV === 'development'
const Debug: typeof import('./GachaDebug') | null =
  // require 조건은 process.env 를 직접 써야 webpack 이 빌드 때 잘라낸다(IS_DEV 로는 안 잘림)
  process.env.NODE_ENV === 'development' ? require('./GachaDebug') : null

const pad = (n: number) => String(n).padStart(2, '0')
const fmt = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
const fmtFull = (d: Date) =>
  `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${fmt(d)}`

/**
 * 배경에 흐르는 글자 줄. 줄마다 반대 방향, 2초에 글자 한 칸(.g-mrow i 너비)씩 움직여 이음매 없이 반복.
 * 줄마다 시작 위치만 조금씩 어긋나게 둔다.
 */
const ROWS = Array.from({ length: 16 }, (_, i) => ({ reverse: i % 2 === 1, delay: ((i * 7) % 10) / 5 }))
const WORDS = Array.from({ length: 6 })

const Marquee: React.FC = () => (
  <div className="g-marquee" aria-hidden>
    {ROWS.map((r, i) => (
      <div
        key={i}
        className={`g-mrow${r.reverse ? ' rev' : ''}`}
        style={{ animationDelay: `-${r.delay}s` }}
      >
        {WORDS.map((_, j) => (
          <i key={j}>TASTEE</i>
        ))}
      </div>
    ))}
  </div>
)

const GachaVoucher: React.FC = () => {
  const navigate = useNavigate()
  // 개발 모드: ?debug= 를 첫 조회 전에 반영
  const [devInit] = useState(() => (IS_DEV && Debug ? Debug.applyVoucherParam() : null))
  const [voucher, setVoucher] = useState<Voucher | null | undefined>(undefined)
  const [now, setNow] = useState(() => new Date())
  const [confirming, setConfirming] = useState(() => !!devInit?.confirm)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    fetchVoucher().then(setVoucher)
  }, [])
  useEffect(load, [load])

  useEffect(() => {
    if (voucher === null) navigate('/event/gacha', { replace: true })
  }, [voucher, navigate])

  const usedAt = voucher?.usedAt ? new Date(voucher.usedAt) : null

  const used = !!usedAt
  useEffect(() => {
    if (used) return
    const t = setInterval(() => setNow(new Date()), 250)
    return () => clearInterval(t)
  }, [used])

  const back = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/event/gacha', { replace: true })
  }

  const redeem = async () => {
    if (busy) return
    setBusy(true)
    const v = await redeemVoucher()
    setBusy(false)
    setConfirming(false)
    if (v) setVoucher(v)
  }

  if (!voucher) return null

  const dialog = confirming && !used && (
    <div className="g-dim" onClick={() => !busy && setConfirming(false)}>
      <div className="g-dialog" role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
        <p className="t">사장님께 보여 주세요</p>
        <p className="d">한 번 사용하면 되돌릴 수 없어요</p>
        <div className="btns">
          <button className="cancel" onClick={() => setConfirming(false)} disabled={busy}>
            취소
          </button>
          <button className="ok" onClick={redeem} disabled={busy}>
            사용하기
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <GachaFrame
      frameClassName={`voucher-bg${used ? ' used' : ''}`}
      backdrop={<Marquee />}
      overlay={dialog}
    >
      {IS_DEV && Debug && (
        <Debug.VoucherDebugBar
          onChange={() => {
            setConfirming(false)
            load()
          }}
        />
      )}
      <div className={`g-voucher${used ? ' used' : ''}`}>
        <button className="g-back" onClick={back} aria-label="뒤로">
          ‹
        </button>
        <h2>교환권</h2>
        <p className="g-lead">당첨을 축하해요!</p>
        <div className="g-vc">
          {usedAt && <div className="g-stamp">사용 완료</div>}
          <div className="g-photo">
            <img src={`${process.env.PUBLIC_URL}/images/gacha/chicken.webp`} alt={voucher.prize} />
          </div>
          <div className="body">
            <div className="prize">{voucher.prize}</div>
            <div className="g-clock">
              {usedAt ? `사용 시각 ${fmtFull(usedAt)}` : `지금 ${fmt(now)}`}
            </div>
            <hr />
            <div className="row">
              <b>사용 매장</b>
              {voucher.store}
            </div>
            <div className="row">
              <b>유효기간</b>
              {voucher.validity}
            </div>
          </div>
        </div>
        {!used && (
          <button className="g-use" onClick={() => setConfirming(true)}>
            사용하기
          </button>
        )}
      </div>
    </GachaFrame>
  )
}

export default GachaVoucher
